import type { Language } from '@/lib/translations'

// Preferencia de idioma del usuario. Vive en una cookie (no en localStorage) para que
// middleware.ts pueda decidir el idioma de `/` y `/legal/*` en el servidor, sin
// redirección en cliente. Guarda el segmento de URL (`gal`), no la clave de
// traducción (`gl`).
export const LANG_COOKIE = 'lang'
export const LANG_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export type LangSegment = 'es' | 'gal' | 'en'

export const LANG_SEGMENTS: readonly LangSegment[] = ['es', 'gal', 'en']

export function isLangSegment(value: string | undefined | null): value is LangSegment {
  return value === 'es' || value === 'gal' || value === 'en'
}

export function segmentToLang(seg: LangSegment): Language {
  return seg === 'gal' ? 'gl' : seg
}

export function langToSegment(lang: Language): LangSegment {
  return lang === 'gl' ? 'gal' : lang
}

// Idioma guardado en la cookie, o `es` si no hay. Solo en cliente.
export function getLangSegment(): LangSegment {
  if (typeof document === 'undefined') return 'es'
  const match = document.cookie.match(new RegExp(`(?:^|; )${LANG_COOKIE}=([^;]*)`))
  const value = match?.[1]
  return isLangSegment(value) ? value : 'es'
}

// Guarda el idioma elegido desde el cliente (selector de idioma de los paneles).
export function setLangSegment(seg: LangSegment): void {
  if (typeof document === 'undefined') return
  document.cookie = `${LANG_COOKIE}=${seg}; path=/; max-age=${LANG_COOKIE_MAX_AGE}; samesite=lax`
}

// Primer idioma soportado de la cabecera `accept-language`, respetando los pesos `q`.
// Sin coincidencia → `es`.
export function pickLangFromAcceptLanguage(header: string | null): LangSegment {
  if (!header) return 'es'
  const prefs = header
    .split(',')
    .map((part, i) => {
      const [tag, ...params] = part.trim().toLowerCase().split(';')
      const q = params.find(p => p.trim().startsWith('q='))
      return { code: tag.split('-')[0], q: q ? parseFloat(q.trim().slice(2)) || 0 : 1, i }
    })
    .filter(p => p.q > 0)
    .sort((a, b) => b.q - a.q || a.i - b.i)

  for (const { code } of prefs) {
    if (code === 'es') return 'es'
    if (code === 'gl') return 'gal'
    if (code === 'en') return 'en'
  }
  return 'es'
}
